import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { FormOption } from 'types';
import { Button, Heading, Input, Text } from 'components';
import { formValidations, setLocalStorageItem } from 'helpers';
import { FontSize, LocalStorageKeys } from 'commons';
import * as S from './styles';

interface Props {
  employeeList: FormOption[];
  setEmployeeList: React.Dispatch<React.SetStateAction<FormOption[]>>;
  handleClose: VoidFunction;
}

export function EmployeeContent({
  employeeList,
  setEmployeeList,
  handleClose,
}: Props) {
  const employeeValidation = Yup.object().shape({
    label: Yup.string().required('Nome'),
    value: Yup.string().required('CPF'),
  });

  const { values, errors, handleSubmit, handleChange, resetForm } =
    useFormik<FormOption>({
      initialValues: {
        label: '',
        value: '',
      },

      validateOnBlur: false,

      validateOnChange: false,

      validationSchema: employeeValidation,

      validate: (values) => {
        formValidations.validationSchema({
          schema: employeeValidation,
          values,
        });
      },

      onSubmit: async (values) => {
        const verifyValue = employeeList.filter(
          ({ value }) => value === values.value
        );

        if (Boolean(verifyValue.length)) {
          toast.warning('CPF já cadastrado', {
            delay: 200,
          });

          return;
        }

        const newEmployeeList = [...employeeList, values].sort((a, b) =>
          a.label.localeCompare(b.label)
        );

        await setEmployeeList(newEmployeeList);

        resetForm();

        handleClose();

        toast.success('Novo vendedor cadastrado', {
          delay: 200,
        });
      },

      onReset: (_, { setValues }) => {},
    });

  function handleDeleteEmployee(employee: FormOption) {
    setEmployeeList((prevState) =>
      prevState.filter((e) => e.value !== employee.value)
    );

    toast.success(`Cadastro de ${employee.label} excluído com sucesso`, {
      delay: 200,
    });

    return;
  }

  useEffect(() => {
    setLocalStorageItem(LocalStorageKeys.EmployeeData, employeeList);
  }, [employeeList]);

  return (
    <S.ContentWrapper>
      <S.TextWrapper>
        <Heading>Cadastro de vendedores</Heading>
        <Text>Insira os dados do vendedor responsável</Text>
      </S.TextWrapper>
      <S.FormWrapper onSubmit={handleSubmit}>
        <S.InputWrapper>
          <S.Label>
            Nome do vendedor<strong>*</strong>
          </S.Label>
          <Input
            name="label"
            placeholder="Insira o nome completo"
            value={values.label}
            type="text"
            onChange={handleChange}
            status={errors.label ? 'error' : 'default'}
          />
        </S.InputWrapper>
        <S.InputWrapper>
          <S.Label>
            CPF do Vendedor<strong>*</strong>
          </S.Label>
          <Input
            name="value"
            placeholder="000.000.000-00"
            type="tel"
            onChange={handleChange}
            value={values.value}
            mask="999.999.999-99"
            status={errors.value ? 'error' : 'default'}
          />
        </S.InputWrapper>
        <Button type="submit" variant="primary" full>
          Salvar
        </Button>
      </S.FormWrapper>
      <S.TextWrapper>
        <Heading>Lista de vendedores</Heading>
      </S.TextWrapper>
      <S.EmployeeList>
        {employeeList &&
          Boolean(employeeList.length) &&
          employeeList.map((employee) => (
            <S.EmployeeCard key={employee.value}>
              <S.EmployeeData>
                <Text size={FontSize.font12}>
                  <strong>{employee.label}</strong>
                </Text>
                <Text size={FontSize.font10}>{employee.value}</Text>
              </S.EmployeeData>
              <S.DeleteEmployee>
                <Button
                  variant="link"
                  onClick={() => handleDeleteEmployee(employee)}
                >
                  Excluir
                </Button>
              </S.DeleteEmployee>
            </S.EmployeeCard>
          ))}
      </S.EmployeeList>
    </S.ContentWrapper>
  );
}
